import React from "react";
import { ConnexisMark } from "@/components/brand/ConnexisMark";
import { Link } from "react-router-dom";

/**
 * PortalShell — the dark navy shell used across the lender + introducer surfaces
 * (pre-login and signed-in). Matches the /hub aesthetic exactly.
 *
 * Slots:
 *  - `top`         : optional right-aligned actions (Sign in / Sign out button etc.)
 *  - `hero`        : big pre-login hero (only shown on marketing/login/apply pages)
 *  - `children`    : main body
 */
export default function PortalShell({
  variant = "signin",       // "signin" | "signed-in" | "apply"
  kind = "lender",          // "lender" | "introducer"
  title,
  subtitle,
  eyebrow,
  ctas,                     // right-aligned nav actions (React nodes)
  children,
}) {
  const isLender = kind === "lender";
  const brandCta = isLender
    ? { label: "Apply as a lender",     href: "/lender/apply",     signin: "/lender/login" }
    : kind === "campaign"
    ? { label: "Partner with us",       href: "/partner-with-us#enquire", signin: "/partner/login" }
    : { label: "Apply as an introducer", href: "/introducer/apply", signin: "/introducer/login" };

  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-100 relative overflow-x-clip overflow-y-visible">
      {/* Ambient background — dot grid, beam, radial glows */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-[70%] bg-gradient-to-r from-transparent via-[#0055FF]/70 to-transparent" />
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#0055FF] opacity-[0.14] blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 h-[420px] w-[420px] rounded-full bg-[#7da9ff] opacity-[0.08] blur-3xl" />
        <div className="absolute top-24 right-[-120px] h-[360px] w-[360px] rounded-full bg-[#0055FF] opacity-[0.10] blur-3xl" />
      </div>

      {/* Top nav — the actions wrap onto a second line on a phone rather than running off-screen */}
      <header className="relative z-10 border-b border-white/[0.06] backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-3 sm:h-16 sm:flex-nowrap sm:px-8 sm:py-0">
          <ConnexisMark mode="dark" />
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 text-[13px]">
            {ctas || (variant === "signin" ? (
              <Link to={brandCta.href} className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center gap-1.5 text-white transition-colors" data-testid="portal-apply-cta">
                {brandCta.label}
              </Link>
            ) : variant === "apply" ? (
              <Link to={brandCta.signin} className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center gap-1.5 text-white transition-colors" data-testid="portal-signin-cta">
                Sign in
              </Link>
            ) : null)}
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 py-10 sm:py-14">
        {(eyebrow || title) && (
          <div className="max-w-2xl mb-10 animate-in fade-in slide-in-from-bottom-3 duration-700">
            {eyebrow && (
              <div className="inline-flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.24em] uppercase text-[#7da9ff]">
                <span className="h-px w-6 bg-[#7da9ff]/60" />
                {eyebrow}
              </div>
            )}
            {title && (
              <h1 className="mt-3 text-[36px] sm:text-[46px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-4 text-[15px] leading-relaxed text-slate-300 max-w-xl">{subtitle}</p>
            )}
          </div>
        )}
        {children}
      </main>

      <footer className="relative z-10 border-t border-white/[0.06] mt-12">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-6 text-[11.5px] text-slate-500 flex items-center justify-between flex-wrap gap-3">
          <div>© {new Date().getFullYear()} Connexis Group Ltd. All rights reserved.</div>
          <div className="flex items-center gap-3">
            <a href="mailto:ops@connex.is" className="hover:text-slate-300">ops@connex.is</a>
            <span className="opacity-40">·</span>
            <span>Introducer &amp; lender surface</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
